import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createOrganization, getOrganization, updateOrganization } from '../../../api/admin'
import { Button } from '../../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../../components/ui/Card'
import { FormField, inputClass } from '../../../components/ui/FormField'
import { useToast } from '../../../components/ui/Toast'
import type { OrganizationCreate } from '../../../types/admin'

export default function OrgForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const toast = useToast()

  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'name' | 'website', string>>>({})

  useEffect(() => {
    if (!isEdit) return
    setLoading(true)
    getOrganization(Number(id))
      .then((org) => {
        setName(org.name)
        setWebsite(org.website ?? '')
        setIsActive(org.is_active)
        setLoading(false)
      })
      .catch(() => {
        setError('Failed to load organisation.')
        setLoading(false)
      })
  }, [id, isEdit])

  const validate = (): boolean => {
    const errs: typeof fieldErrors = {}
    if (!name.trim()) errs.name = 'Name is required.'
    if (website && !/^https?:\/\/.+/.test(website.trim())) errs.website = 'Enter a valid URL starting with http:// or https://.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!validate()) return
    setSubmitting(true)
    setError(null)
    const payload: OrganizationCreate = {
      name: name.trim(),
      website: website.trim() || undefined,
      is_active: isActive,
    }
    try {
      if (isEdit) {
        await updateOrganization(Number(id), payload)
        toast.success('Organisation updated.')
      } else {
        await createOrganization(payload)
        toast.success('Organisation created.')
      }
      void navigate('/admin/organizations')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 p-8">Loading…</div>

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate('/admin/organizations')}
          className="bg-transparent border-0 cursor-pointer text-accent text-sm font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="text-[1.4rem] font-bold text-text-heading m-0">
          {isEdit ? 'Edit Organisation' : 'New Organisation'}
        </h1>
      </div>

      <Card>
        <SectionTitle>Details</SectionTitle>
        {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <FormField label="Name" required error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: undefined })) }}
              className={inputClass(!!fieldErrors.name)}
            />
          </FormField>

          <FormField label="Website" error={fieldErrors.website} hint="Optional — include https://">
            <input
              type="url"
              value={website}
              onChange={(e) => { setWebsite(e.target.value); setFieldErrors(p => ({ ...p, website: undefined })) }}
              placeholder="https://example.com"
              className={inputClass(!!fieldErrors.website)}
            />
          </FormField>

          <FormField label="Status">
            <label className="flex items-center gap-2 cursor-pointer text-[0.9rem]">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>
          </FormField>

          <div className="flex gap-3 mt-7 pt-6 border-t border-border">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Organisation'}
            </Button>
            <Button type="button" variant="outline" onClick={() => void navigate('/admin/organizations')}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
